import { jsxs as _jsxs } from "react/jsx-runtime";
import { jsx as _jsx } from "react/jsx-runtime";
import { useCallback, useEffect, useRef, useState } from 'react';
import { render } from 'react-dom';
import PortalIdParser from 'PortalIdParser';
import { stringify } from 'hub-http/helpers/params';
import { FMMessageType, IframeComponentTypes, MessageActions, PANEL_WIDTH_EXPANDED, PickerPanels } from '../constants';
import FileManagerContext from './FileManagerContext';
import PanelIframe from './PanelIframe';
import PanelSkeleton from './PanelSkeleton';
import { createWrapperEl, fireEvent, getCurrentApp, getIframeAppOrigin, isDeployed, isFileManagerMessage, logDebug, logNewRelicError, trackBreadcrumb, truthyObject, uniqueId } from './utils';
const wrapperEl = createWrapperEl();
const IFRAME_LOAD_TIMEOUT_MS = 15 * 1000;

const IframeContainer = ({
  children,
  uploadedFileAccess,
  disableUpload,
  withCanva,
  withShutterstock,
  specificCanvaTemplates
}) => {
  const panelWidth = PANEL_WIDTH_EXPANDED;
  const [pickerType, setPickerType] = useState();
  const [iframeLoaded, setIframeLoaded] = useState(false);
  const [panelOpen, setPanelOpen] = useState(false);
  const [fullScreen, setFullScreen] = useState(false);
  const [loadingFailed, setLoadingFailed] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const iframeRef = useRef(null);
  const iframeLoadTimeoutRef = useRef();
  const pickerProps = useRef();
  const imageEditorProps = useRef();
  const activeComponent = useRef(); // track an incrementing id for each mount to help debug multiple pickers mounted at once, iframe load timeouts, etc

  const uniqueIdRef = useRef();

  const log = (...args) => logDebug(`(${uniqueIdRef.current || ''})`, ...args);

  const logBreadcrumb = (message, data) => {
    trackBreadcrumb(`(${[uniqueIdRef.current]}) ${message}`, data);
  };

  const getProps = () => {
    return activeComponent.current === IframeComponentTypes.FILE_PICKER ? pickerProps.current : imageEditorProps.current;
  };

  const buildIframeUrl = () => {
    const params = {
      uploadedFileAccess,
      hostApp: getCurrentApp()
    };

    if (disableUpload) {
      params.disableUpload = true;
    }

    if (withCanva) {
      params.withCanva = true;
    }

    if (withShutterstock) {
      params.withShutterstock = true;
    }

    if (Array.isArray(specificCanvaTemplates)) {
      params.specificCanvaTemplates = specificCanvaTemplates.join(',');
    }

    const iframeOrigin = getIframeAppOrigin();

    if (!isDeployed()) {
      params.localApp = true;
    } else if (iframeOrigin !== window.origin && iframeOrigin !== 'about:blank') {
      params.hostDeployed = true;
    }

    const baseUrl = `${iframeOrigin}/file-picker/${PortalIdParser.get()}`;
    return `${baseUrl}?${stringify(params)}`;
  };

  const isIframeLoaded = () => {
    return Boolean(iframeRef.current && iframeRef.current.dataset.loaded);
  };

  const createIframe = onRendered => {
    // take care to reuse the iframe we create on initial mount on subsequent opens,
    // so we use a separate root instead of children / portals and "reparent" the iframeRef
    const existingIframeEl = wrapperEl.querySelector('iframe');

    if (existingIframeEl && document.contains(existingIframeEl)) {
      if (isIframeLoaded()) {
        log('Found existing iframe which has already loaded', existingIframeEl);
      } else {
        log('Found existing iframe (not yet loaded)', existingIframeEl);
      }

      iframeRef.current = existingIframeEl;
      onRendered();
      return;
    }

    document.body.appendChild(wrapperEl);
    log('Creating iframe with url', buildIframeUrl());
    render( /*#__PURE__*/_jsx(PanelIframe, {
      ref: iframeRef,
      iframeUrl: buildIframeUrl(),
      panelWidth: panelWidth,
      pickerType: pickerType,
      fullScreen: fullScreen,
      logBreadcrumb: logBreadcrumb
    }), wrapperEl, onRendered);
  };

  const showIframe = () => {
    wrapperEl.style.display = 'block';
  };

  const hideIframe = () => {
    wrapperEl.style.display = 'none';
    setPanelOpen(false);
  };

  const handleTimeout = () => {
    if (isIframeLoaded()) {
      return;
    }

    hideIframe();
    setLoadingFailed(true);
    const err = new Error('[file-manager] Timeout loading picker iframe');
    logNewRelicError(err);
    setTimeout(() => {
      throw err;
    });
  };

  const postMessageToIframe = useCallback((action, payload = {}) => {
    const targetOrigin = getIframeAppOrigin();
    const iframeContentWindow = iframeRef.current ? iframeRef.current.contentWindow : null;

    if (!iframeContentWindow) {
      log('Could not access iframe contentWindow');
      return;
    }

    log('Posting message to iframe', action, payload, targetOrigin);
    iframeContentWindow.postMessage({
      messageType: FMMessageType,
      instanceId: uniqueIdRef.current,
      action,
      payload
    }, targetOrigin === 'about:blank' ? '*' : targetOrigin);
  }, []);
  const sendOpenMessage = useCallback(() => {
    const props = pickerProps.current;

    if (!props) {
      console.error('sendOpenMessage called without pickerProps');
      return;
    }

    const payload = truthyObject({
      type: props.type,
      disableUpload: props.disableUpload,
      filterType: props.filterType,
      filteredExtensions: props.filteredExtensions,
      filteredReasons: props.filteredReasons,
      withBulkImageImport: props.withBulkImageImport,
      renderContext: props.renderContext,
      suggestedImageGenPrompt: props.suggestedImageGenPrompt,
      imageGenerationPromptCommand: props.imageGenerationPromptCommand
    });

    if (props.initialPanel && PickerPanels[props.initialPanel]) {
      payload.initialPanel = props.initialPanel;
    }

    logBreadcrumb('Sending OPEN_PICKER message', payload);
    showIframe();
    postMessageToIframe(MessageActions.OPEN_PICKER, payload);
    fireEvent('FileManagerLib:Drawer:open');
  }, [postMessageToIframe]);
  const sendImageEditorOpenMessage = useCallback(() => {
    const props = imageEditorProps.current;

    if (!props) {
      console.error('sendImageEditorOpenMessage called without props');
      return;
    }

    const payload = {
      fileId: props.fileId,
      cropperMode: props.cropperMode,
      fixedAspectRatio: props.fixedAspectRatio,
      simpleCropOnly: props.simpleCropOnly,
      saveEncoding: props.saveEncoding,
      uploadedFileAccess: props.uploadedFileAccess
    };
    logBreadcrumb('Sending OPEN_IMAGE_EDITOR message', payload);
    showIframe();
    postMessageToIframe(MessageActions.OPEN_IMAGE_EDITOR, payload);
    setFullScreen(true);
    fireEvent('FileManagerLib:Drawer:open');
  }, [postMessageToIframe]);
  const onIframeReady = useCallback(() => {
    if (activeComponent.current === IframeComponentTypes.IMAGE_EDITOR) {
      sendImageEditorOpenMessage();
    } else {
      sendOpenMessage();
    }

    if (iframeLoadTimeoutRef.current) {
      clearTimeout(iframeLoadTimeoutRef.current);
    }

    setLoadingFailed(false);
  }, [sendOpenMessage, sendImageEditorOpenMessage]);

  const initIframe = () => {
    createIframe(() => {
      if (isIframeLoaded()) {
        if (wrapperEl.style.display !== 'none') {
          logBreadcrumb('Mounted with visible iframe (unexpected)');
        } else {
          logBreadcrumb('Mounted with existing iframe');
        }

        if (!iframeLoaded) {
          onIframeReady();
          setIframeLoaded(true);
        }
      } else {
        logBreadcrumb('Mounted with new iframe');

        if (!iframeLoadTimeoutRef.current) {
          iframeLoadTimeoutRef.current = setTimeout(handleTimeout, IFRAME_LOAD_TIMEOUT_MS);
        }
      }
    });
  };

  const openPicker = useCallback(props => {
    activeComponent.current = IframeComponentTypes.FILE_PICKER;
    pickerProps.current = props;
    uniqueIdRef.current = uniqueIdRef.current || uniqueId();

    if (!iframeRef.current) {
      const existingIframeEl = wrapperEl.querySelector('iframe');
      iframeRef.current = existingIframeEl;
    }

    if (props.pickerType) {
      setPickerType(props.pickerType);
    }

    if (isIframeLoaded()) {
      showIframe(); // ensures currently visible iframe reacts to prop change

      sendOpenMessage();
    } else {
      initIframe();
    }

    setPanelOpen(true);
  }, // eslint-disable-next-line react-hooks/exhaustive-deps
  []);
  const openImageEditor = useCallback(props => {
    activeComponent.current = IframeComponentTypes.IMAGE_EDITOR;
    imageEditorProps.current = props;
    uniqueIdRef.current = uniqueIdRef.current || uniqueId();

    if (!iframeRef.current) {
      const existingIframeEl = wrapperEl.querySelector('iframe');
      iframeRef.current = existingIframeEl;
    }

    if (isIframeLoaded()) {
      // ensures currently visible iframe reacts to prop change
      sendImageEditorOpenMessage();
    } else {
      initIframe();
    }
  }, // eslint-disable-next-line react-hooks/exhaustive-deps
  []);

  const handleMessage = e => {
    const {
      action,
      payload,
      instanceId
    } = e.data;

    if (!isFileManagerMessage(e)) {
      return;
    } // indicates the message was intended for another component instance, so should be ignored
    // (one iframe element is shared across all previously & currently mounted instances)


    if (instanceId && uniqueIdRef.current && instanceId !== uniqueIdRef.current) {
      return;
    }

    log('Received message from iframe', e.data);
    logBreadcrumb(`Received ${action}`, payload);

    switch (action) {
      case MessageActions.IFRAME_READY:
        if (iframeRef.current) {
          iframeRef.current.dataset.loaded = 'true';
        } else {
          log('Received IFRAME_READY message but missing iframe ref');
          return;
        }

        onIframeReady();
        setIframeLoaded(true);
        break;

      case MessageActions.ENTER_FULLSCREEN:
        setFullScreen(true);
        break;

      case MessageActions.EXIT_FULLSCREEN:
        setFullScreen(false);
        break;

      case MessageActions.UPLOAD_STARTED:
        setIsUploading(true);
        break;

      case MessageActions.UPLOAD_COMPLETE:
        setIsUploading(false);
        break;

      case MessageActions.CLOSE_PANEL:
        {
          hideIframe();
          const props = getProps();

          if (props && props.onClose) {
            props.onClose();
          }

          break;
        }

      case MessageActions.SELECT_FILE:
        hideIframe();

        if (!payload.file) {
          return;
        }

        if (pickerProps.current) {
          pickerProps.current['onSelect'](payload.file);
        } else if (imageEditorProps.current) {
          imageEditorProps.current['onSaveComplete'](payload.file.url, payload.file);
        }

        break;

      default:
        break;
    }
  };

  const onMessageReceived = useCallback(handleMessage, [onIframeReady]);
  useEffect(() => {
    window.addEventListener('message', onMessageReceived);
    uniqueIdRef.current = uniqueIdRef.current || uniqueId();
    return () => {
      logBreadcrumb('IframeContainer unmounted');

      if (iframeLoadTimeoutRef.current) {
        clearTimeout(iframeLoadTimeoutRef.current);
      }

      window.removeEventListener('message', onMessageReceived);
      fireEvent('FileManagerLib:Drawer:close');
    }; // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  useEffect(() => {
    if (isUploading) {
      log(`Uploads started...`);
    }

    const handleBeforeUnload = e => {
      log('handleBeforeUnload', e, isUploading);

      if (isUploading) {
        e.preventDefault();
        e.returnValue = '';
      }
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, [isUploading]);
  useEffect(() => {
    // calling render on the same root element will update
    render( /*#__PURE__*/_jsx(PanelIframe, {
      ref: iframeRef,
      iframeUrl: buildIframeUrl(),
      panelWidth: panelWidth,
      pickerType: pickerType,
      fullScreen: fullScreen,
      logBreadcrumb: logBreadcrumb
    }), wrapperEl); // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pickerType, panelWidth, fullScreen]);
  const handleClose = useCallback(() => {
    logBreadcrumb('closePicker called');
    postMessageToIframe(MessageActions.CLOSE_PICKER);
    setPanelOpen(false);
    hideIframe();
    const props = getProps();

    if (props && props.onClose) {
      props.onClose();
    }
  }, [postMessageToIframe]);
  const handleSkeletonClose = useCallback(() => {
    logBreadcrumb('PanelSkeleton onClose called');
    handleClose();
  }, [handleClose]);
  return /*#__PURE__*/_jsxs(FileManagerContext.Provider, {
    value: {
      iframeLoaded,
      openPicker,
      openImageEditor,
      closePicker: handleClose,
      uploadedFileAccess
    },
    children: [pickerType && panelOpen && /*#__PURE__*/_jsx(PanelSkeleton, {
      iframeLoaded: iframeLoaded,
      loadingFailed: loadingFailed,
      pickerType: pickerType,
      panelWidth: panelWidth,
      onClose: handleSkeletonClose
    }), children]
  });
};

IframeContainer.displayName = 'FileManagerIframeContainer';
export default IframeContainer;